import { Link, useLocation, useNavigate } from 'react-router-dom'
import { Badge } from 'primereact/badge'
import { Ripple } from 'primereact/ripple'
import { Tooltip } from 'primereact/tooltip'
import { classNames } from 'primereact/utils'
import React, { useEffect, useState } from 'react'
import { useAuth } from '../context/auth-context'
import { useLayout } from '../context/layout-context'
import { useSignalR } from '../context/signalr-context'
import { useIsMobile } from 'shared/hooks/use-is-mobile'
import { VERSION_INFO } from 'version'

interface MenuItem {
  label: string
  icon?: string
  to?: string
  badge?: string
  items?: MenuItem[]
  badgeClassName?: string
  description?: string
  command?: (event: {
    originalEvent: React.SyntheticEvent
    item: MenuItem
  }) => void
}

const AppMenu = () => {
  const { layoutState, setLayoutState } = useLayout()
  const location = useLocation()
  const pathName = location.pathname
  const navigate = useNavigate()
  const [activeItem, setActiveItem] = useState<MenuItem | null>(null)
  const [loadingItem, setLoadingItem] = useState<string | null>(null)
  const isMobile = useIsMobile()
  const { user, logout } = useAuth()
  const { unreadAlarmCount } = useSignalR()
  const [isPending, setIsPending] = useState(false)

  useEffect(() => {
    setLoadingItem(null)
    setIsPending(false)
  }, [pathName])

  const menuItems: MenuItem[] = [
    {
      label: 'Dashboard',
      icon: 'pi pi-home',
      to: '/',
      description: 'Visão geral do sistema',
    },
    {
      label: 'Alarmes',
      icon: 'pi pi-shield',
      to: '/alarms',
      badge: unreadAlarmCount > 0 ? String(Math.min(unreadAlarmCount, 99)) : undefined,
      badgeClassName: 'p-badge-danger',
      description: 'Configurar alertas de segurança',
    },
    {
      label: 'Eventos Veiculos',
      icon: 'pi pi-chart-bar',
      to: '/anpr-events',
      description: 'Histórico de detecções',
    },
    {
      label: 'Eventos Pessoas',
      icon: 'pi pi-chart-bar',
      to: '/ahcr-events',
      description: 'Histórico de detecções',
    },
    {
      label: 'Câmeras Live',
      icon: 'pi pi-chart-line',
      to: '/stream',
      description: 'Câmeras ao vivo',
    },
    {
      label: 'Mapa',
      icon: 'pi pi-map-marker',
      to: '/map',
      description: 'Localização e status das câmeras',
    },
    {
      label: 'Saúde das Câmeras',
      icon: 'pi pi-heart-fill',
      to: '/camera-health',
      description: 'Última detecção e status por câmera',
    },
    {
      label: 'Histórico de Placa',
      icon: 'pi pi-search',
      to: '/plate-history',
      description: 'Todas as passagens de uma placa',
    },
  ]

  const isActiveRoute = (item: MenuItem): boolean => pathName === item.to

  const onMenuItemClick = (item: MenuItem, e: React.MouseEvent): void => {
    e.preventDefault()
    if (pathName === item.to) return

    setActiveItem(item)
    setLoadingItem(item.to as string)
    setIsPending(true)

    if (layoutState.staticMenuMobileActive || layoutState.overlayMenuActive) {
      setLayoutState((prev) => ({
        ...prev,
        staticMenuMobileActive: false,
        overlayMenuActive: false,
      }))
    }

    navigate(item.to as string)
  }

  const isMenuVisible = isMobile
    ? layoutState.staticMenuMobileActive || layoutState.overlayMenuActive
    : true

  const menuClassName = classNames('enhanced-menu', {
    'menu-transitioning': isPending,
  })

  if (isMobile && !isMenuVisible) return null

  return (
    <>
      <Tooltip target='.menu-item' />
      {isMobile && isMenuVisible && (
        <div
          className='mobile-menu-overlay'
          onClick={() =>
            setLayoutState((prev) => ({
              ...prev,
              staticMenuMobileActive: false,
              overlayMenuActive: false,
            }))
          }
        />
      )}

      <div className={menuClassName}>
        {/* Mobile-only close button for the full-screen drawer */}
        <button
          type='button'
          className='menu-close-btn'
          aria-label='Fechar menu'
          onClick={() =>
            setLayoutState((prev) => ({
              ...prev,
              staticMenuMobileActive: false,
              overlayMenuActive: false,
            }))
          }
        >
          <i className='pi pi-times' />
        </button>

        {/* Brand Logo — matches signin page pattern */}
        <div className='menu-logo'>
          <div className='menu-brand'>
            <div className='menu-brand-logo-slot'>
              <img
                src='/logo.png'
                alt='RT Guard'
                className='menu-brand-logo-img'
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                }}
              />
              <div className='menu-brand-icon-fallback'>
                <i className='pi pi-shield'></i>
              </div>
            </div>
            <div className='menu-brand-text'>
              <span className='menu-brand-name'>RT GUARD</span>
              <span className='menu-brand-tag'>SEGURANÇA EM TEMPO REAL</span>
            </div>
          </div>
        </div>

        <div className='menu-navigation'>
          <div className='menu-section-title'>
            <span>NAVEGAÇÃO</span>
          </div>

          <div className='menu-items'>
            {menuItems.map((item, index) => {
              const isActive = isActiveRoute(item)
              const isLoading = loadingItem === item.to

              return (
                <div
                  key={index}
                  className='menu-item-wrapper'
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <Link
                    to={item.to as string}
                    onClick={(e) => onMenuItemClick(item, e)}
                    style={{ textDecoration: 'none' }}
                  >
                    <div
                      className={classNames('menu-item', {
                        'menu-item--active': isActive,
                        'menu-item--loading': isLoading || isPending,
                      })}
                      data-pr-tooltip={item.description}
                      data-pr-position='right'
                    >
                      <div className='menu-item-content'>
                        <div className='menu-icon-container'>
                          {isLoading ? (
                            <i className='pi pi-spin pi-spinner menu-spinner' />
                          ) : (
                            <i className={item.icon} />
                          )}
                          {item.badge && !isLoading && (
                            <Badge
                              value={item.badge}
                              severity='danger'
                              className='menu-badge'
                            />
                          )}
                        </div>

                        <div className='menu-label-container'>
                          <span className='menu-label'>
                            {isLoading ? 'Carregando...' : item.label}
                          </span>
                        </div>

                        {isActive && !isLoading && (
                          <div className='active-indicator'>
                            <div className='active-dot'></div>
                          </div>
                        )}
                      </div>
                      <Ripple />
                    </div>
                  </Link>
                </div>
              )
            })}
          </div>
        </div>

        <div className='menu-footer'>
          <div className='user-profile' onClick={() => logout()}>
            <div className='user-avatar'>
              <i className='pi pi-user'></i>
            </div>
            <div className='user-info'>
              <span className='user-name'>{user?.name || user?.login}</span>
              <span className='user-role'>Administrador</span>
              <span className='menu-version-tag'>{VERSION_INFO.version}</span>
            </div>
            <div className='user-status'>
              <div className='status-dot online'></div>
            </div>
          </div>
        </div>

        <a
          href='https://bsctecnologia.com.br/'
          target='_blank'
          rel='noopener noreferrer'
        >
          <div className='layout-footer'>
            <span className='font-medium ml-2 animated'>BSC Tecnologia</span>
          </div>
        </a>
      </div>
    </>
  )
}

export default AppMenu
